'use client';
import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface SideBandsGroupEditDialogProps {
  open: boolean;
  onClose: () => void;
  initialLabelPrefix: string;
  initialCenterPosition: number;
  initialSidebandPosition: number;
  initialCount: number;
  xAxisType: 'date' | 'numeric' | 'category';
  onSave: (labelPrefix: string, centerPosition: number, sidebandPosition: number, count: number) => void;
  position?: { x: number; y: number };
  xRange?: { min: number; max: number }; // Optional x-axis range for validation
  replaceMarkers?: boolean; // If true, creating side bands will replace existing markers
  isNewGroup?: boolean; // If true, this is creating a new group (not editing)
}

/**
 * Popover for editing an existing side bands group
 */
export const SideBandsGroupEditDialog: React.FC<SideBandsGroupEditDialogProps> = ({
  open,
  onClose,
  initialLabelPrefix,
  initialCenterPosition,
  initialSidebandPosition,
  initialCount,
  xAxisType,
  onSave,
  position,
  xRange,
  replaceMarkers = false,
  isNewGroup = false,
}) => {
  const [labelPrefix, setLabelPrefix] = useState(initialLabelPrefix);
  const [centerPosition, setCenterPosition] = useState(initialCenterPosition);
  const [sidebandPosition, setSidebandPosition] = useState(initialSidebandPosition);
  const [count, setCount] = useState(initialCount);
  const [sidebandError, setSidebandError] = useState<string | null>(null);

  // Calculate interval (distance) from center to sideband
  const interval = Math.abs(sidebandPosition - centerPosition);

  // String states for inputs to allow free typing (formatted to 1 decimal)
  const [centerPositionInput, setCenterPositionInput] = useState(initialCenterPosition.toFixed(1));
  const [intervalInput, setIntervalInput] = useState(interval.toFixed(1));

  const popoverRef = useRef<HTMLDivElement>(null);
  const formatTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Validate sideband position is within x-axis range
  const validateSideband = (sidebandValue: number, centerValue: number, countValue: number) => {
    if (!xRange) {
      setSidebandError(null);
      return;
    }

    const interval = Math.abs(sidebandValue - centerValue);
    const range = xRange.max - xRange.min;

    // Check if the calculated interval would put carriers outside range
    if (interval * countValue > range) {
      setSidebandError(`Sideband too far. Max: ${(centerValue + range / countValue).toFixed(1)}`);
    } else if (sidebandValue === centerValue) {
      setSidebandError('Sideband must be different from center');
    } else {
      setSidebandError(null);
    }
  };

  useEffect(() => {
    if (open) {
      setLabelPrefix(initialLabelPrefix);
      setCenterPosition(initialCenterPosition);
      setSidebandPosition(initialSidebandPosition);
      setCount(initialCount);

      // Calculate initial interval
      const initialInterval = Math.abs(initialSidebandPosition - initialCenterPosition);

      // Reset input strings with 1 decimal formatting
      setCenterPositionInput(initialCenterPosition.toFixed(1));
      setIntervalInput(initialInterval.toFixed(1));

      // Validate initial sideband
      validateSideband(initialSidebandPosition, initialCenterPosition, initialCount);
    }
  }, [open, initialLabelPrefix, initialCenterPosition, initialSidebandPosition, initialCount]);

  // Click outside handler
  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 100);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      // Clean up any pending format timer
      if (formatTimerRef.current) {
        clearTimeout(formatTimerRef.current);
      }
    };
  }, [open, onClose]);

  const handleSave = () => {
    onSave(labelPrefix, centerPosition, sidebandPosition, count);
    onClose();
  };

  if (!open) {
    return null;
  }

  return (
    <div
      ref={popoverRef}
      style={{
        position: 'fixed',
        left: position?.x || 100,
        top: position?.y || 100,
        zIndex: 9999,
        maxHeight: 'calc(100vh - 40px)',
        overflowY: 'auto',
      }}
    >
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl border border-[#B1B9C3] dark:border-gray-700 p-4 w-[240px]">
        <h3 className="text-sm font-semibold mb-4 text-[#01152B] dark:text-gray-100">Edit Side Bands</h3>

        <div className="space-y-3">
          {/* Position */}
          <div className="flex items-center justify-between">
            <Label htmlFor="position" className="text-xs font-normal text-[#55687D] dark:text-gray-400">
              Position
            </Label>
            <Input
              id="position"
              type="text"
              value={centerPositionInput}
              onChange={(e) => {
                setCenterPositionInput(e.target.value);
                // Clear any pending format timer
                if (formatTimerRef.current) {
                  clearTimeout(formatTimerRef.current);
                }
              }}
              onFocus={() => {
                // Clear any pending format timer when field is focused
                if (formatTimerRef.current) {
                  clearTimeout(formatTimerRef.current);
                }
              }}
              onBlur={(e) => {
                const value = e.target.value;
                // Debounce the formatting to avoid interrupting rapid field navigation
                formatTimerRef.current = setTimeout(() => {
                  const parsed = parseFloat(value);
                  if (!isNaN(parsed)) {
                    setCenterPosition(parsed);
                    // Format to 1 decimal
                    setCenterPositionInput(parsed.toFixed(1));
                    // Update interval display with new center position
                    const newInterval = Math.abs(sidebandPosition - parsed);
                    setIntervalInput(newInterval.toFixed(1));
                    // Revalidate with new center position
                    validateSideband(sidebandPosition, parsed, count);
                  } else {
                    // Reset to last valid value with 1 decimal
                    setCenterPositionInput(centerPosition.toFixed(1));
                  }
                }, 200);
              }}
              className="w-[120px] text-center text-xs h-8 bg-[#F1F2F4] dark:bg-gray-700 border-0 text-[#01152B] dark:text-gray-100 font-semibold focus:ring-0 focus:outline-none px-2"
            />
          </div>

          {/* Interval/Distance - distance between center and first sideband */}
          <div className="flex flex-col gap-1">
            <div className="flex items-center justify-between">
              <Label htmlFor="interval" className="text-xs font-normal text-[#55687D] dark:text-gray-400">
                Distance
              </Label>
              <Input
                id="interval"
                type="text"
                value={intervalInput}
                onChange={(e) => {
                  setIntervalInput(e.target.value);
                  // Clear any pending format timer
                  if (formatTimerRef.current) {
                    clearTimeout(formatTimerRef.current);
                  }
                }}
                onFocus={() => {
                  // Clear any pending format timer when field is focused
                  if (formatTimerRef.current) {
                    clearTimeout(formatTimerRef.current);
                  }
                }}
                onBlur={(e) => {
                  const value = e.target.value;
                  // Debounce the formatting to avoid interrupting rapid field navigation
                  formatTimerRef.current = setTimeout(() => {
                    const parsed = parseFloat(value);
                    if (!isNaN(parsed) && parsed > 0) {
                      // Calculate new sideband position from interval
                      // Maintain the direction (positive or negative)
                      const wasPositive = sidebandPosition >= centerPosition;
                      const newSidebandPosition = wasPositive
                        ? centerPosition + parsed
                        : centerPosition - parsed;

                      setSidebandPosition(newSidebandPosition);
                      // Format to 1 decimal
                      setIntervalInput(parsed.toFixed(1));
                      validateSideband(newSidebandPosition, centerPosition, count);
                    } else if (parsed === 0) {
                      setSidebandError('Distance must be greater than 0');
                      setIntervalInput('0.0');
                    } else {
                      // Reset to last valid interval with 1 decimal
                      const currentInterval = Math.abs(sidebandPosition - centerPosition);
                      setIntervalInput(currentInterval.toFixed(1));
                    }
                  }, 200);
                }}
                className={`w-[120px] text-center text-xs h-8 bg-[#F1F2F4] dark:bg-gray-700 ${
                  sidebandError
                    ? 'border-2 border-red-500 dark:border-red-400'
                    : 'border-0'
                } text-[#01152B] dark:text-gray-100 font-semibold focus:ring-0 focus:outline-none px-2`}
              />
            </div>
            {sidebandError && (
              <div className="text-[10px] text-red-600 dark:text-red-400 text-right">
                {sidebandError}
              </div>
            )}
          </div>

          {/* Name */}
          <div className="flex items-center justify-between">
            <Label htmlFor="name" className="text-xs font-normal text-[#55687D] dark:text-gray-400">
              Name
            </Label>
            <Input
              id="name"
              type="text"
              value={labelPrefix}
              maxLength={4}
              onChange={(e) => setLabelPrefix(e.target.value.toUpperCase())}
              className="w-[120px] text-center text-xs h-8 bg-[#F1F2F4] dark:bg-gray-700 border-0 text-[#01152B] dark:text-gray-100 font-semibold focus:ring-0 focus:outline-none px-2"
              placeholder="C"
            />
          </div>

          {/* Count */}
          <div className="flex items-center justify-between">
            <Label htmlFor="count" className="text-xs font-normal text-[#55687D] dark:text-gray-400">
              Count
            </Label>
            <div className="flex items-center justify-between bg-[#F1F2F4] dark:bg-gray-700 rounded-md border-0 h-8 px-3 w-[120px]">
              <button
                type="button"
                onClick={() => {
                  const newCount = Math.max(1, count - 1);
                  setCount(newCount);
                  // Re-validate with new count
                  validateSideband(sidebandPosition, centerPosition, newCount);
                }}
                className="text-[#55687D] dark:text-gray-400 hover:text-[#01152B] dark:hover:text-gray-200 text-base leading-none flex items-center justify-center flex-shrink-0"
              >
                −
              </button>
              <div className="w-[20px] h-full flex items-center justify-center flex-shrink-0 flex-grow-0">
                <span className="text-xs font-semibold text-[#01152B] dark:text-gray-100">
                  {count}
                </span>
              </div>
              <button
                type="button"
                onClick={() => {
                  const newCount = Math.min(20, count + 1);
                  setCount(newCount);
                  // Re-validate with new count
                  validateSideband(sidebandPosition, centerPosition, newCount);
                }}
                className="text-[#55687D] dark:text-gray-400 hover:text-[#01152B] dark:hover:text-gray-200 text-base leading-none flex items-center justify-center flex-shrink-0"
              >
                +
              </button>
            </div>
          </div>

          {/* Info Text */}
          {isNewGroup && replaceMarkers && (
            <div className="pt-2 pb-1">
              <p className="text-[10px] text-amber-600 dark:text-amber-400 leading-tight">
                Creating this group will replace existing markers
              </p>
            </div>
          )}
          {isNewGroup && !replaceMarkers && (
            <div className="pt-2 pb-1">
              <p className="text-[10px] text-blue-600 dark:text-blue-400 leading-tight">
                Creating this group will keep existing markers
              </p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex items-center justify-end gap-2 pt-3 border-t border-[#E6E8EC] dark:border-gray-700">
            <Button
              variant="ghost"
              onClick={onClose}
              className="h-8 px-4 text-xs font-normal text-[#55687D] dark:text-gray-400 hover:text-[#01152B] dark:hover:text-gray-200 hover:bg-transparent"
            >
              Close
            </Button>
            <Button
              onClick={handleSave}
              disabled={!!sidebandError}
              className="h-8 px-4 text-xs font-semibold bg-[#6667DD] hover:bg-[#5556CC] text-white border-0 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[#6667DD]"
            >
              Apply
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
